import { useEffect, useState } from "react"
import { X, Search, Copy, Download } from "lucide-react"
import assetNoTranscript from '@/assets/noTranscript.svg'
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
} from "@/clientWeb/components/ui/drawer"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { meetingsApi } from "../../services/meetings.api"
import { formatMeetingTime } from "../../services/meetings.service"

interface TranscriptEntry {
  speaker: string
  time: string
  text: string
}

function getInitials(name: string) {
  return name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
}

const COLORS = [
  "bg-primary/20 text-primary",
  "bg-violet-500/20 text-violet-600",
  "bg-emerald-500/20 text-emerald-600",
  "bg-amber-500/20 text-amber-600",
  "bg-rose-500/20 text-rose-600",
]

function SpeakerAvatar({ name }: { name: string }) {
  const idx = name.charCodeAt(0) % COLORS.length
  return (
    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${COLORS[idx]}`}>
      {getInitials(name)}
    </div>
  )
}

interface TranscriptionHistoryProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Meeting uuid or readable code — the server resolves either. */
  meetingId?: string | null
}

export default function TranscriptionHistory({ open, onOpenChange, meetingId }: TranscriptionHistoryProps) {
  const t = useTranslation()
  const [search, setSearch] = useState("")
  const [entries, setEntries] = useState<TranscriptEntry[]>([])
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)

  // Fetch on open rather than on mount, same as ChatHistory: the drawer is
  // rendered alongside the meeting details, so mounting it is not a sign
  // anyone wants to read it.
  useEffect(() => {
    if (!open || !meetingId) return
    let cancelled = false

    setLoading(true)
    setFailed(false)
    meetingsApi
      .getTranscripts(meetingId)
      .then((res) => {
        if (cancelled) return
        setEntries(
          res.transcripts.map((s) => ({
            speaker: s.speakerName ?? "",
            time: formatMeetingTime(s.ts),
            text: s.text,
          })),
        )
      })
      .catch(() => {
        // Distinguished from "no transcript" below — an empty transcript and
        // an unreachable server should not look the same to the reader.
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, meetingId])

  const filtered = search.trim()
    ? entries.filter(e =>
        e.text.toLowerCase().includes(search.toLowerCase()) ||
        e.speaker.toLowerCase().includes(search.toLowerCase())
      )
    : entries

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text)
  }

  const handleDownload = () => {
    const content = entries.map(e => `[${e.time}] ${e.speaker}: ${e.text}`).join("\n")
    const blob = new Blob([content], { type: "text/plain" })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = "transcript.txt"
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent direction="right" width="sm:max-w-[400px]">
        {/* Header */}
        <DrawerHeader className="px-5 py-3.5 border-b border-stroke-100 dark:border-border dark:border-slate-800 flex flex-row items-center justify-between shrink-0">
          <DrawerTitle className="text-base font-semibold text-slate-800 dark:text-white">
            {t("meetings.transcription")}
          </DrawerTitle>
          <div className="flex items-center gap-2">
            {entries.length > 0 && (
              <button
                onClick={handleDownload}
                className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted transition-colors"
                title={t("meetings.downloadTranscript")}
              >
                <Download className="w-4 h-4" />
              </button>
            )}
            <DrawerClose asChild>
              <button className="w-8 h-8 rounded-full flex items-center justify-center text-primary border border-primary hover:bg-primary/10 transition-all duration-200">
                <X className="w-4 h-4" />
              </button>
            </DrawerClose>
          </div>
        </DrawerHeader>

        {/* Search */}
        <div className="px-5 py-3 border-b border-stroke-100 dark:border-border dark:border-slate-800 shrink-0">
          <div className="flex items-center gap-2 bg-slate-50 dark:bg-muted/40 rounded-xl px-3 py-2">
            <Search className="w-4 h-4 text-muted-foreground shrink-0" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder={t("meetings.searchTranscript")}
              className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* Entries */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-center text-muted-foreground py-12">{t("common.loading")}</p>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-4 text-center py-12">
              <img src={assetNoTranscript} alt="No results" className="w-24 h-24 opacity-60" />
              <div>
                <p className="text-sm text-muted-foreground">
                  {failed
                    ? t("meetings.transcriptHistoryUnavailable")
                    : search
                      ? `${t("common.noResultsFor")} "${search}"`
                      : t("meetings.noTranscriptAvailable")}
                </p>
                {!search && !failed && (
                  <p className="text-xs text-muted-foreground/70 mt-1">{t("meetings.transcriptsNotSaved")}</p>
                )}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {filtered.map((entry, i) => {
                // Empty for a guest the server has no name for — same
                // fallback ChatHistory uses for an unnamed sender.
                const speaker = entry.speaker || t("panels.unknown")
                return (
                  <div key={i} className="flex gap-3 group">
                    <SpeakerAvatar name={speaker} />
                    <div className="flex-1 bg-slate-50 dark:bg-card rounded-xl border border-stroke-100 dark:border-border dark:border-border/50 px-3 py-2">
                      <div className="flex items-baseline gap-2 mb-0.5">
                        <span className="text-xs font-semibold text-foreground">{speaker}</span>
                        <span className="text-[10px] text-muted-foreground font-mono">{entry.time}</span>
                        <button
                          className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-muted"
                          onClick={() => handleCopy(entry.text)}
                          title={t("common.copy")}
                        >
                          <Copy className="w-3 h-3 text-muted-foreground" />
                        </button>
                      </div>
                      <p className="text-sm text-foreground leading-relaxed">{entry.text}</p>
                    </div>
                  </div>
                )
              })}
              <p className="text-[10px] text-muted-foreground/40 text-center pt-4 border-t border-border/30">
                {t("meetings.autoGeneratedDisclaimer")}
              </p>
            </div>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  )
}
